import { useEffect, useMemo, useState } from 'react';

interface CountdownResult {
	timeLeftMs: number;
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
	isExpired: boolean;
}

export const useCountdown = (targetDate: Date | string | number | null | undefined, interval = 1000): CountdownResult => {
	const targetTimestamp = useMemo(() => {
		if (!targetDate) {
			return 0;
		}
		const parsed = typeof targetDate === 'number' ? targetDate : new Date(targetDate).getTime();
		return Number.isFinite(parsed) ? parsed : 0;
	}, [targetDate]);

	const [timeLeft, setTimeLeft] = useState(() => Math.max(0, targetTimestamp - Date.now()));

	useEffect(() => {
		if (!targetTimestamp) {
			setTimeLeft(0);
			return;
		}

		setTimeLeft(Math.max(0, targetTimestamp - Date.now()));

		const timer = setInterval(() => {
			setTimeLeft((prev) => {
				const next = Math.max(0, targetTimestamp - Date.now());
				return next === prev ? prev : next;
			});
		}, interval);

		return () => {
			clearInterval(timer);
		};
	}, [targetTimestamp, interval]);

	const totalSeconds = Math.floor(timeLeft / 1000);
	const days = Math.floor(totalSeconds / (60 * 60 * 24));
	const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
	const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
	const seconds = Math.max(0, totalSeconds % 60);

	return {
		timeLeftMs: timeLeft,
		days,
		hours,
		minutes,
		seconds,
		isExpired: timeLeft <= 0,
	};
};
